import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import '../common-styles.css';
import '../shared/GradientHero.css';
import GradientHero from '../shared/GradientHero';
import giaImg from '../../assets/images/grading/gia.png';
import igiImg from '../../assets/images/grading/igi.png';
import hrdImg from '../../assets/images/grading/hrd.png';
import './CertificationsPage.css';

const CertificationsPage: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.replace('#', ''));
    const el = document.getElementById(id);
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  return (
    <div className="certifications-page">
      <GradientHero
        title={<><span>Certified by Trust,</span><br/>Backed by Standards</>}
        backTo="/"
        badgeText="CERTIFICATIONS"
        subtitleHighlight="Global Grading," 
        subtitleRest=" Guaranteed Quality"
        lead={<>At SunDiam, every diamond is evaluated under internationally recognized standards, primarily following the Gemological Institute of America (GIA), along with IGI, HRD, and other reputed laboratories. Our commitment to certified quality ensures each stone carries the assurance of authenticity, precision, and transparency — giving you complete confidence in every facet.</>}
        className="grading-hero"
      />

      <section className="grading-details-section">
        <Container>
          {/* GIA */}
          <div id="gia-certification" className="grading-block mb-5">
            <Row className="align-items-stretch">
              <Col lg={7} className="grading-text d-flex flex-column order-1 order-lg-1">
                <div>
                  <h3 className="grading-agency-name">GIA Certification</h3>
                  <p className="grading-agency-desc">
                    The Gemological Institute of America (GIA) is the world's foremost authority in diamond grading and certification. Renowned for its scientific rigor and integrity, GIA sets the global standard for evaluating a diamond's cut, color, clarity, and carat weight. Its certification ensures complete transparency and helps buyers make informed, confident decisions.
                  </p>
                </div>
                <div className="mt-auto">
                  <a href="https://www.gia.edu/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button className="section-btn"><span className="btn-content">Read More <i className="fas fa-arrow-right"/></span></Button>
                  </a>
                </div>
              </Col>
              <Col lg={5} className="grading-image-col d-flex align-items-center order-2 order-lg-2">
                <div className="grading-image-wrapper">
                  <img src={giaImg} alt="GIA" className="grading-agency-img" />
                </div>
              </Col>
            </Row>
          </div>

          {/* IGI */}
          <div id="igi-certification" className="grading-block mb-5">
            <Row className="align-items-stretch">
              <Col lg={5} className="grading-image-col d-flex align-items-center order-2 order-lg-1">
                <div className="grading-image-wrapper">
                  <img src={igiImg} alt="IGI" className="grading-agency-img" />
                </div>
              </Col>
              <Col lg={7} className="grading-text d-flex flex-column order-1 order-lg-2">
                <div>
                  <h3 className="grading-agency-name">IGI Certification</h3>
                  <p className="grading-agency-desc">
                    The International Gemological Institute (IGI) is a globally respected authority in diamond and gemstone certification. Known for its consistency and comprehensive grading reports, IGI provides clear, detailed assessments of a diamond's quality. Its certifications are widely recognized in the jewelry industry, offering assurance of authenticity and value to buyers and sellers alike.
                  </p>
                </div>
                <div className="mt-auto">
                  <a href="https://www.igi.org/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button className="section-btn"><span className="btn-content">Read More <i className="fas fa-arrow-right"/></span></Button>
                  </a>
                </div>
              </Col>
            </Row>
          </div>

          {/* HRD */}
          <div id="hrd-certification" className="grading-block mb-5">
            <Row className="align-items-stretch">
              <Col lg={7} className="grading-text d-flex flex-column order-1 order-lg-1">
                <div>
                  <h3 className="grading-agency-name">HRD Certification</h3>
                  <p className="grading-agency-desc">
                    HRD Antwerp is one of Europe's leading diamond certification laboratories, known for its precision, reliability, and adherence to strict grading standards. Backed by decades of expertise, HRD certificates provide detailed insight into a diamond's characteristics, ensuring confidence and transparency in every transaction. Their grading reports are trusted across global markets for both loose and set diamonds.
                  </p>
                </div>
                <div className="mt-auto">
                  <a href="https://hrdantwerp.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button className="section-btn"><span className="btn-content">Read More <i className="fas fa-arrow-right"/></span></Button>
                  </a>
                </div>
              </Col>
              <Col lg={5} className="grading-image-col d-flex align-items-center order-2 order-lg-2">
                <div className="grading-image-wrapper">
                  <img src={hrdImg} alt="HRD" className="grading-agency-img" />
                </div>
              </Col>
            </Row>
          </div>

          {/* Additional Section */}
          <div className="grading-conclusion">
            <h3 className="grading-extra-title">Bridging Visual Excellence <span className="section-title-highlight">with Practical Grading</span></h3>
            <p className="grading-extra-text">
              At SunDiam, we understand that not all diamonds align perfectly with lab-based grading systems—yet still possess exceptional visual appeal. Some stones, while not qualifying for certification by institutes like GIA due to specific technical parameters, remain eye-clean, well-cut, and of high overall quality.
              <br/><br/>
              To ensure these diamonds are not overlooked, we follow our Pre-GIA internal evaluation system. It reflects our commitment to both aesthetic value and practical transparency, allowing customers to access beautiful diamonds that offer exceptional brilliance at a compelling value.
            </p>
          </div>

          <div className="text-center mt-5">
            <Link to="/" className="back-link-inline">&larr; Back to Home</Link>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default CertificationsPage;
